import { defineRouting } from "next-intl/routing";

/**
 * i18n routing config — single source of truth for locale handling.
 *
 * Pattern: `localePrefix: 'always'` matches the FR-founded SaaS consensus
 * (Qonto, PayFit, OVHcloud, Back Market, WTTJ all use this). Both `/fr/...`
 * and `/en/...` are always prefixed; `/` 308-redirects to `/fr` via
 * next.config.ts redirects.
 *
 * Spec: ~/.claude/specs/2026-05-15-i18n-overhaul-design.md §2.2
 */
export const routing = defineRouting({
  locales: ["fr", "en"],
  defaultLocale: "fr",
  localePrefix: "always",
  localeCookie: {
    name: "NEXT_LOCALE",
    maxAge: 60 * 60 * 24 * 365, // 1 year
    sameSite: "lax",
    // HSTS protects us already but `secure` is best practice: forbids the
    // cookie from being sent over HTTP even on a fresh load that bypasses
    // HSTS (e.g. first visit). Also satisfies the OWASP cookie checklist.
    secure: true,
  },
  // Disable next-intl's automatic `Link` response header. Its default emits
  // `hreflang="x-default"` pointing at the UNPREFIXED root (e.g. velyq.com/blog),
  // which 308-redirects back to the prefixed canonical (velyq.com/fr/blog) —
  // a circular signal that GSC flags as "Page avec redirection / Échec" on
  // the prefixed destinations. We emit hreflang via Next's metadata API
  // (lib/seo/locale.ts -> localizedAlternates) with x-default = /fr.
  alternateLinks: false,
});

export type Locale = (typeof routing.locales)[number];
